import { useEffect, useRef } from 'react';
import { Banknote, Check, CreditCard, Cylinder, Loader2, Package, Star, Wrench, X } from 'lucide-react';

import {
  useStripeMethods,
  useAutopayEnrollments,
  useSetAutopay,
  useSetStripeMethodRole,
  type AutopayScope,
} from '@/hooks/useStripeMethods';
import { useRentalItems } from '@/hooks/useRentalAutopayItems';
import { stripeMethodLabel } from '@/lib/stripe';

/**
 * StripeCardPurposeModal — "What is this card for?" Shown right after a card is
 * saved (and reopenable via "Edit uses" on each saved method). Lets the
 * customer choose, per purpose, whether THIS card covers it — and for rentals,
 * that means autopay (policy: cylinder + equipment rentals are billed
 * automatically). Purposes the customer doesn't have are hidden.
 *
 * Model: per-scope autopay enrollment (orders.autopay_enrollments via
 * useSetAutopay) + the MAIN PAYER role (is_default, useSetStripeMethodRole) —
 * one name for that flag everywhere it is shown: here, Settings → Payment
 * methods, /pay, and the chain pay screen.
 *
 * `autoDefault` (set when opened right after a SAVE, not "Edit uses"): the new
 * card becomes the main payer and is auto-enrolled for any rental
 * scope the customer has that isn't already covered — so a customer just adds
 * a card and rentals/cylinders are on it, no extra clicks. They can change it
 * here. Never overrides a scope already pointed at another card.
 */

export function StripeCardPurposeModal({
  stripePmId,
  onClose,
  autoDefault = false,
}: {
  stripePmId: string;
  onClose: () => void;
  autoDefault?: boolean;
}) {
  const methodsQuery = useStripeMethods(true);
  const method = (methodsQuery.data?.methods ?? []).find((m) => m.stripe_pm_id === stripePmId) ?? null;

  const enrollQuery = useAutopayEnrollments(true);
  const roster = useRentalItems();
  const setAutopay = useSetAutopay();
  const setRole = useSetStripeMethodRole();

  const hasEquipment = (roster.data ?? []).some((r) => r.kind === 'equipment');
  const hasCylinders = (roster.data ?? []).some((r) => r.kind === 'tank');

  const enrollmentFor = (scope: AutopayScope) =>
    (enrollQuery.data?.enrollments ?? []).find((e) => e.scope === scope && e.active) ?? null;
  const isOnThisCard = (scope: AutopayScope) => enrollmentFor(scope)?.stripe_pm_id === stripePmId;

  const busy = setAutopay.isPending || setRole.isPending;
  const loading = methodsQuery.isLoading || enrollQuery.isLoading || roster.isLoading;

  // On a fresh save: default this card + turn on the rental scopes the customer
  // has that no card covers yet. Runs once, after the data loads.
  const autoRan = useRef(false);
  useEffect(() => {
    if (!autoDefault || autoRan.current || loading || !method) return;
    autoRan.current = true;
    if (!method.is_default) setRole.mutate({ stripe_pm_id: stripePmId, role: 'default' });
    const scopes: AutopayScope[] = [];
    if (hasEquipment) scopes.push('equipment');
    if (hasCylinders) scopes.push('cylinder');
    for (const scope of scopes) {
      if (!enrollmentFor(scope)) setAutopay.mutate({ scope, stripe_pm_id: stripePmId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDefault, loading, method, hasEquipment, hasCylinders]);

  function toggleScope(scope: AutopayScope, on: boolean) {
    if (on) setAutopay.mutate({ scope, stripe_pm_id: stripePmId });
    else setAutopay.mutate({ scope, off: true });
  }

  const label = method ? stripeMethodLabel(method) : 'this card';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-2xl bg-brix-surface shadow-card-deep overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-brix-border bg-brix-surface-alt/60">
          <h2 className="text-lg font-bold text-brix-ink flex items-center gap-2">
            {method?.kind === 'ach' ? <Banknote size={20} className="text-brix-primary" /> : <CreditCard size={20} className="text-brix-primary" />}
            What is this card for?
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-brix-muted hover:bg-brix-primary-tint hover:text-brix-ink transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <p className="text-base text-slate-700 leading-relaxed">
            Choose what <span className="font-bold text-slate-900">{label}</span> should be used for.
            You can change any of this later under <span className="font-semibold">Payment methods</span>.
          </p>

          {(hasCylinders || hasEquipment) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[15px] leading-relaxed text-amber-900">
              Cylinder and equipment rentals must stay on <span className="font-bold">autopay</span>.
              Pick the card that covers each — you can switch cards anytime, but one card has to cover it.
            </div>
          )}

          {loading ? (
            <p className="text-base text-brix-muted flex items-center gap-2 py-6 justify-center">
              <Loader2 size={18} className="animate-spin" /> Loading…
            </p>
          ) : (
            <div className="space-y-3">
              {hasEquipment && (
                <PurposeRow
                  icon={Wrench}
                  title="Equipment rentals"
                  blurb="Monthly equipment / fountain rental invoices — paid automatically (autopay)."
                  on={isOnThisCard('equipment')}
                  busy={busy}
                  onToggle={(v) => toggleScope('equipment', v)}
                  requiredNote={!enrollmentFor('equipment') ? 'No card covers equipment rentals yet.' : undefined}
                />
              )}

              {hasCylinders && (
                <PurposeRow
                  icon={Cylinder}
                  title="Cylinder rentals"
                  blurb="Monthly CO₂ / cylinder (tank) rental invoices — paid automatically (autopay)."
                  on={isOnThisCard('cylinder')}
                  busy={busy}
                  onToggle={(v) => toggleScope('cylinder', v)}
                  requiredNote={!enrollmentFor('cylinder') ? 'No card covers cylinder rentals yet.' : undefined}
                />
              )}

              {/* Product — always available. Two independent choices. */}
              <div className="rounded-2xl border border-brix-border bg-brix-surface p-4">
                <div className="flex items-center gap-2">
                  <Package size={18} className="text-brix-primary" />
                  <span className="text-base font-bold text-slate-900">Product orders</span>
                  <span className="rounded-md border border-brix-border bg-brix-surface-alt px-2 py-0.5 text-xs font-semibold text-brix-muted">
                    Optional
                  </span>
                </div>
                <p className="mt-1 text-sm text-brix-muted leading-relaxed">
                  Beverage &amp; product invoices. Autopay is optional here.
                </p>
                <div className="mt-3 space-y-2.5">
                  <CheckRow
                    label="Automatically pay product invoices (autopay)"
                    sub="We'll charge this card when a product invoice is due."
                    checked={isOnThisCard('product')}
                    disabled={busy}
                    onChange={(v) => toggleScope('product', v)}
                  />
                </div>
              </div>

              {/* The MAIN PAYER is not product-scoped — it is the card charged
                  whenever an invoice is paid by hand, whichever kind — so it
                  sits on its own rather than inside the Product orders card,
                  which is what it used to read as. */}
              <div className="rounded-2xl border border-brix-border bg-brix-surface p-4">
                <div className="flex items-center gap-2">
                  <Star size={18} className="text-brix-primary" />
                  <span className="text-base font-bold text-slate-900">Main payer</span>
                </div>
                <p className="mt-1 text-sm text-brix-muted leading-relaxed">
                  The card we charge when you pay an invoice yourself, unless you pick a
                  different one at the time.
                </p>
                <div className="mt-3">
                  <CheckRow
                    label="Make this the main payer"
                    sub="Any invoice you pay by hand — product, rental or a whole chain's stores."
                    checked={method?.is_default === true}
                    disabled={busy || method?.is_default === true}
                    onChange={() => setRole.mutate({ stripe_pm_id: stripePmId, role: 'default' })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-brix-border bg-brix-surface-alt/40">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-3 rounded-xl bg-brix-primary text-white text-base font-bold hover:bg-brix-primary-deep transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
            disabled={busy}
          >
            {busy ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />} Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** A rental purpose row — a single on/off switch (on = this card on autopay). */
function PurposeRow({
  icon: Icon,
  title,
  blurb,
  on,
  busy,
  onToggle,
  requiredNote,
}: {
  icon: typeof Wrench;
  title: string;
  blurb: string;
  on: boolean;
  busy: boolean;
  onToggle: (on: boolean) => void;
  requiredNote?: string;
}) {
  return (
    <div className="rounded-2xl border border-brix-border bg-brix-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={18} className="text-brix-primary shrink-0" />
            <span className="text-base font-bold text-slate-900">{title}</span>
            <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
              Required
            </span>
          </div>
          <p className="mt-1 text-sm text-brix-muted leading-relaxed">{blurb}</p>
          {on ? (
            <p className="mt-1.5 text-sm font-semibold text-emerald-700">This card covers it.</p>
          ) : requiredNote ? (
            <p className="mt-1.5 text-sm font-semibold text-amber-800">{requiredNote}</p>
          ) : null}
        </div>
        <Switch on={on} disabled={busy} onClick={() => onToggle(!on)} />
      </div>
    </div>
  );
}

/** Larger checkbox-style row for the product options. */
function CheckRow({
  label,
  sub,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  sub: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 accent-brix-primary disabled:opacity-60"
      />
      <span className="min-w-0">
        <span className="block text-base font-semibold text-slate-900 leading-snug">{label}</span>
        <span className="block text-sm text-brix-muted leading-relaxed">{sub}</span>
      </span>
    </label>
  );
}

/** Accessible on/off switch. */
function Switch({ on, disabled, onClick }: { on: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        on ? 'bg-emerald-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          on ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}
